import React, { createContext, useContext, useState, useEffect, ReactNode, useCallback } from 'react';
import { useAppSelector } from 'app';
import MessagingAPI from 'components/messaging/api/messaging-api';
import { ApplicationPropertyNames } from 'common/utils/shared/shared-constants';

interface SocialTestDealerContextType {
  isSocialTestDealer: boolean;
  isCheckingSocialTestDealer: boolean;
  refreshSocialTestDealerStatus: () => Promise<void>;
}

const SocialTestDealerContext = createContext<SocialTestDealerContextType | undefined>(undefined);

export const SocialTestDealerProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [isSocialTestDealer, setIsSocialTestDealer] = useState<boolean>(false);
  const [isCheckingSocialTestDealer, setIsCheckingSocialTestDealer] = useState<boolean>(true);
  const dmsDealerId = useAppSelector((state) => state.user.dmsDealerId);

  const checkSocialTestDealer = useCallback(async () => {
    if (!dmsDealerId) {
      setIsSocialTestDealer(false);
      setIsCheckingSocialTestDealer(false);
      return;
    }

    try {
      setIsCheckingSocialTestDealer(true);
      const response = await MessagingAPI.getApplicationProperty(ApplicationPropertyNames.SocialTestDealers);
      if (response?.propertyValue) {
        const testDealerNumbers = response.propertyValue.split(',').map((d: string) => d.trim());
        setIsSocialTestDealer(
          testDealerNumbers.includes('*') || testDealerNumbers.includes(dmsDealerId?.toString())
        );
      } else {
        setIsSocialTestDealer(false);
      }
    } catch (error) {
      console.error('Error checking social test dealer:', error);
      setIsSocialTestDealer(false);
    } finally {
      setIsCheckingSocialTestDealer(false);
    }
  }, [dmsDealerId]);

  useEffect(() => {
    checkSocialTestDealer();
  }, [checkSocialTestDealer]);

  const refreshSocialTestDealerStatus = useCallback(async () => {
    await checkSocialTestDealer();
  }, [checkSocialTestDealer]);

  return (
    <SocialTestDealerContext.Provider
      value={{
        isSocialTestDealer,
        isCheckingSocialTestDealer,
        refreshSocialTestDealerStatus,
      }}>
      {children}
    </SocialTestDealerContext.Provider>
  );
};

export const useSocialTestDealer = (): SocialTestDealerContextType => {
  const context = useContext(SocialTestDealerContext);
  if (!context) {
    throw new Error('useSocialTestDealer must be used within a SocialTestDealerProvider');
  }
  return context;
};

export default SocialTestDealerContext;
